/**
 * ToolPalette — palette d'outils flottante et déplaçable.
 *
 * Reprend les entrées du menu « Ajout » sous forme d'icônes, et introduit la
 * notion d'OUTIL ACTIF (jusqu'ici inexistante dans l'éditeur) : Sélection —
 * le comportement historique —, Gomme, Zone et Main.
 *
 * MISE EN GRILLE
 * Deux colonnes de cellules carrées strictement identiques. Ce n'est pas un
 * détail cosmétique : avec des libellés côte à côte, la longueur des mots
 * (« Rond » contre « Bibliothèque de formes ») désaligne les icônes et l'œil
 * ne peut plus balayer la colonne. Le nom de chaque outil vit dans son
 * infobulle, et le menu « Ajout » garde la version libellée.
 *
 * Les deux sections comptent 4 et 8 entrées, soit 2 et 4 rangées pleines —
 * aucune case orpheline. L'arrivée de la Main a refermé le trou que laissait la
 * troisième entrée, et c'est une raison de plus de ne pas passer les modes sur
 * trois colonnes : la palette resterait large de 38 px que la section
 * « Insérer » laisserait vides à chaque rangée.
 *
 * Portal vers document.body au palier z 60 (« UI flottante ancrée au canvas »,
 * cf. l'échelle documentée dans globals.css) : la palette échappe ainsi au
 * stacking context du canvas transformé, tout en passant sous le Studio I.A
 * (110) et les modales (140).
 */
import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLabelStore } from '../store/useLabelStore';

const CELL = 'w-[34px] h-[34px] flex items-center justify-center rounded-md transition-colors';

// Micro-titre de section — 9 px MAJUSCULES tracking large (charte LabelForge).
function SectionLabel({ children }) {
    return (
        <div className="text-[9px] font-mono uppercase tracking-widest text-[rgba(var(--forge-muted),0.55)] px-1 pt-2 pb-1 select-none">
            {children}
        </div>
    );
}

const ICONS = {
    select: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="m3 3 7.07 16.97 2.51-7.39 7.39-2.51L3 3z" />
        </svg>
    ),
    eraser: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="m7 21-4.3-4.3a1 1 0 0 1 0-1.4l9.6-9.6a2 2 0 0 1 2.8 0l4.9 4.9a2 2 0 0 1 0 2.8L13 21" />
            <path d="M22 21H7" />
            <path d="m5 11 9 9" />
        </svg>
    ),
    hand: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 11V6a2 2 0 0 0-2-2a2 2 0 0 0-2 2" />
            <path d="M14 10V4a2 2 0 0 0-2-2a2 2 0 0 0-2 2v2" />
            <path d="M10 10.5V6a2 2 0 0 0-2-2a2 2 0 0 0-2 2v8" />
            <path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15" />
        </svg>
    ),
    // Rectangle en pointillé : la convention universelle du « marching ants ».
    marquee: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeDasharray="3.2 2.6">
            <rect x="3.5" y="5.5" width="17" height="13" />
        </svg>
    ),
    text: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="4 7 4 4 20 4 20 7" /><line x1="9" y1="20" x2="15" y2="20" /><line x1="12" y1="4" x2="12" y2="20" />
        </svg>
    ),
    image: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="m21 15-5-5L5 21" />
        </svg>
    ),
    barcode: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 5v14M8 5v14M12 5v14M16 5v14M21 5v14" />
        </svg>
    ),
    rect: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="3" y="3" width="18" height="18" rx="2" /></svg>
    ),
    circle: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="12" cy="12" r="9" /></svg>
    ),
    roundedRect: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="3" y="3" width="18" height="18" rx="6" /></svg>
    ),
    line: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="3" y1="12" x2="21" y2="12" /></svg>
    ),
    shapes: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
        </svg>
    ),
};

export default function ToolPalette({ onAddText, onAddImage, onAddBarcode, onAddShape, onOpenShapeLibrary }) {
    const activeTool = useLabelStore((s) => s.activeTool);
    const setActiveTool = useLabelStore((s) => s.setActiveTool);
    const palette = useLabelStore((s) => s.toolPalette);
    const setToolPalette = useLabelStore((s) => s.setToolPalette);

    const dragRef = useRef(null);
    const boxRef = useRef(null);

    // Premier affichage : se poser contre le bord gauche de la zone de canvas,
    // là où l'œil cherche naturellement une palette d'outils.
    useLayoutEffect(() => {
        if (palette.x !== null && palette.y !== null) return;
        const host = document.getElementById('canvas-viewport');
        const r = host?.getBoundingClientRect();
        setToolPalette({
            x: Math.round((r?.left ?? 320) + 16),
            y: Math.round((r?.top ?? 140) + 16),
        });
    }, [palette.x, palette.y, setToolPalette]);

    // Déplacement à la poignée. Le clamp final garantit qu'on ne peut jamais
    // pousser la palette hors de l'écran au point de ne plus la rattraper.
    useEffect(() => {
        const onMove = (e) => {
            const d = dragRef.current;
            if (!d) return;
            const w = boxRef.current?.offsetWidth ?? 100;
            const h = boxRef.current?.offsetHeight ?? 240;
            setToolPalette({
                x: Math.min(window.innerWidth - w - 4, Math.max(4, d.baseX + e.clientX - d.startX)),
                y: Math.min(window.innerHeight - 40, Math.max(4, d.baseY + e.clientY - d.startY)),
            });
        };
        const onUp = () => {
            if (!dragRef.current) return;
            dragRef.current = null;
            document.body.style.cursor = '';
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
        return () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };
    }, [setToolPalette]);

    const startDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragRef.current = { startX: e.clientX, startY: e.clientY, baseX: palette.x ?? 0, baseY: palette.y ?? 0 };
        document.body.style.cursor = 'grabbing';
    };

    if (!palette.open || palette.x === null) return null;

    const modes = [
        { id: 'select', icon: ICONS.select, label: 'Sélection', hint: 'Sélectionner et déplacer (V)' },
        { id: 'eraser', icon: ICONS.eraser, label: 'Gomme', hint: 'Gommer une image (E)' },
        { id: 'marquee', icon: ICONS.marquee, label: 'Sélection de zone', hint: 'Sélectionner une zone dans une image (M, puis M pour changer de forme)' },
        { id: 'hand', icon: ICONS.hand, label: 'Main', hint: 'Déplacer la vue (H — barre d\'espace pour la flèche le temps d\'un geste)' },
    ];

    const inserts = [
        { icon: ICONS.text, hint: 'Texte', onClick: onAddText },
        { icon: ICONS.image, hint: 'Image…', onClick: onAddImage },
        { icon: ICONS.barcode, hint: 'Code-barres', onClick: onAddBarcode },
        { icon: ICONS.rect, hint: 'Carré', onClick: () => onAddShape('rect') },
        { icon: ICONS.circle, hint: 'Rond', onClick: () => onAddShape('circle') },
        { icon: ICONS.roundedRect, hint: 'Rectangle arrondi', onClick: () => onAddShape('rounded-rect') },
        { icon: ICONS.line, hint: 'Ligne / Trait', onClick: () => onAddShape('line') },
        { icon: ICONS.shapes, hint: 'Bibliothèque de formes…', onClick: onOpenShapeLibrary },
    ];

    return createPortal(
        <div
            ref={boxRef}
            role="toolbar"
            aria-label="Palette d'outils"
            style={{ position: 'fixed', top: palette.y, left: palette.x, zIndex: 60 }}
            className="bg-forge-bg/95 backdrop-blur-2xl border border-forge-border/40 rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.5)] select-none"
            onMouseDown={(e) => e.stopPropagation()}
        >
            {/* Poignée + fermeture */}
            <div
                onMouseDown={startDrag}
                title="Déplacer la palette"
                className="flex items-center justify-between gap-1 px-1.5 py-1 border-b border-white/10 cursor-grab active:cursor-grabbing rounded-t-xl hover:bg-white/5"
                style={{ touchAction: 'none' }}
            >
                <svg width="14" height="8" viewBox="0 0 14 8" fill="currentColor" className="text-[rgba(var(--forge-muted),0.4)]">
                    <circle cx="3" cy="2.5" r="1" /><circle cx="7" cy="2.5" r="1" /><circle cx="11" cy="2.5" r="1" />
                    <circle cx="3" cy="6" r="1" /><circle cx="7" cy="6" r="1" /><circle cx="11" cy="6" r="1" />
                </svg>
                <button
                    type="button"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={() => setToolPalette({ open: false })}
                    title="Fermer la palette (Tab pour la rouvrir)"
                    aria-label="Fermer la palette d'outils"
                    className="w-4 h-4 flex items-center justify-center rounded text-[rgba(var(--forge-muted),0.5)] hover:text-[rgba(var(--forge-text),1)] hover:bg-white/10 transition-colors"
                >
                    <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                </button>
            </div>

            <div className="px-1.5 pb-2">
                <SectionLabel>Modes</SectionLabel>
                <div className="grid grid-cols-2 gap-1">
                    {modes.map((m) => {
                        const on = activeTool === m.id;
                        return (
                            <button
                                key={m.id}
                                type="button"
                                onClick={() => setActiveTool(m.id)}
                                title={m.hint}
                                aria-label={m.label}
                                aria-pressed={on}
                                className={`${CELL} ${on
                                    ? 'bg-forge-accent text-white shadow-[0_0_12px_rgba(var(--forge-accent),0.35)]'
                                    : 'text-[rgba(var(--forge-muted),0.75)] hover:text-[rgba(var(--forge-text),1)] hover:bg-white/10'}`}
                            >
                                {m.icon}
                            </button>
                        );
                    })}
                </div>

                <SectionLabel>Insérer</SectionLabel>
                <div className="grid grid-cols-2 gap-1">
                    {inserts.map((it) => (
                        <button
                            key={it.hint}
                            type="button"
                            onClick={() => { setActiveTool('select'); it.onClick?.(); }}
                            title={it.hint}
                            aria-label={it.hint}
                            className={`${CELL} text-[rgba(var(--forge-muted),0.75)] hover:text-[rgba(var(--forge-text),1)] hover:bg-white/10`}
                        >
                            {it.icon}
                        </button>
                    ))}
                </div>
            </div>
        </div>,
        document.body
    );
}
